import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Button,
  Form,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  APIgetClient,
  APIupdateClient,
} from '../../../api/Clients';

import AdminPanelAccountEditForm from '../AccountEditForm';
import AdminPanelAccountEditFeatures from '../AccountEditFeatures';
import ProcessingSpinner from '../../ProcessingSpinner';

import { addFlashMessage } from '../../../helpers/FlashMessages';

import ErrorBoundary from '../../ErrorBoundary';
import Loading from '../../Loading';

const AdminPanelAccountsEditModal = ({
  features,
  clientUuid,
  handleClose,
  afterClientUpdated,
}) => {
  const [client, setClient] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const intl = useIntl();

  const getPaymentType = (c) => {
    if (c.is_complimentary) {
      return 'complimentary';
    }
    if (c.has_manual_subscription) {
      return 'manual';
    }
    return null;
  };

  useEffect(() => {
    setLoading(true);
    APIgetClient(clientUuid).then((response) => {
      setClient({
        ...response,
        features: response.features || [],
        payment_type: getPaymentType(response),
        g2_review_button_hidden: parseInt(response.settings?.g2_review_button_hidden || 0),
        desktop_sync_enabled: parseInt(response.settings?.desktop_sync_enabled || 0),
        public_api_enabled: parseInt(response.settings?.public_api_enabled || 0),
        pricing_date: response.settings?.pricing_date || '',
      });
      setLoading(false);
    });
  }, [clientUuid]);

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    if (type === 'checkbox') {
      setClient(prevState => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      setClient(prevState => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleChangeFeatures = (e) => {
    const {
      name,
      checked,
    } = e.target;

    if (checked) {
      const newFeature = features.find(f => (f.slug === name));
      if (newFeature) {
        const newFeatures = [...client.features];
        newFeatures.push(newFeature);
        setClient(prevState => ({
          ...prevState,
          features: newFeatures,
        }));
      }
    } else {
      setClient(prevState => ({
        ...prevState,
        features: prevState.features.filter(f => (f.slug !== name)),
      }));
    }
  };

  const handleSave = () => {
    const verrors = [];
    if (!client.subdomain) {
      verrors.push('subdomain');
    }
    setErrors(verrors);
    if (verrors.length > 0) {
      return;
    }

    setSaving(true);
    const params = {
      subdomain: client.subdomain,
      name: client.name,
      type: client.type,
      payment_type: client.payment_type,
      storage_limit: client.storage_limit,
      trial_ends_at: client.trial_ends_at,
      features: client.features.map(f => (f.slug)),
      discount: client.discount,
      g2_review_button_hidden: client.g2_review_button_hidden,
      desktop_sync_enabled: client.desktop_sync_enabled,
      public_api_enabled: client.public_api_enabled,
      pricing_date: client.pricing_date,
    };
    APIupdateClient(client.uuid, params).then((response) => {
      afterClientUpdated(response);
      setSaving(false);
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({
          defaultMessage: 'Your changes have been saved',
        }),
      });
    });
  };

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose}>
        {intl.formatMessage({ defaultMessage: 'Manage client' })}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <Loading />
        ) : (
          <>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'general' })}
                  href="#general"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('general');
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'General' })}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'features' })}
                  href="#features"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('features');
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'Features' })}
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={activeTab} className="admin-panel-modal-tab-content pt-3">
              <Form className="admin-account-edit-form m-0">
                <TabPane tabId="general">
                  <AdminPanelAccountEditForm
                    client={client}
                    errors={errors}
                    handleChange={handleChange}
                  />
                </TabPane>
                <TabPane tabId="features">
                  <AdminPanelAccountEditFeatures
                    client={client}
                    handleChange={handleChangeFeatures}
                  />
                </TabPane>
              </Form>
            </TabContent>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          {!saving && (
            <Button
              color="secondary"
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
          )}
          <Button
            className="confirmation-button"
            color="primary"
            disabled={saving || errors.length > 0}
            onClick={handleSave}
          >
            <ProcessingSpinner show={saving} />
            {saving ?
              intl.formatMessage({ defaultMessage: 'Saving...' }) :
              intl.formatMessage({ defaultMessage: 'Save' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  features: state.App.features,
});

AdminPanelAccountsEditModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  features: PropTypes.array.isRequired,
  afterClientUpdated: PropTypes.func.isRequired,
  clientUuid: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(AdminPanelAccountsEditModal);
